/* False positives - ESLint thinks we're calling a hook from a class component. */
/* eslint-disable react-hooks/rules-of-hooks */
import * as React from 'react';
import { Store } from "./Store.js";
import { useStore } from "./useStore.js";
import { useEventCallback } from "../useEventCallback.js";
import { useIsoLayoutEffect } from "../useIsoLayoutEffect.js";
import { NOOP } from "../empty.js";

/**
 * A Store that supports controlled state keys.
 *
 * - Keys registered through {@link useControlledProp} become controlled when a non-undefined
 *   value is provided. Controlled keys mirror the incoming value and ignore local writes
 *   (via {@link set}, {@link apply}, or {@link update}).
 * - When a key is uncontrolled, an optional default value is written once on first render.
 * - Use {@link useSyncedValue} and {@link useSyncedValues} to synchronize external values/props into the
 *   store during a layout phase using {@link useIsoLayoutEffect}.
 */
export class ReactStore extends Store {
  constructor(state, context = {}, selectors) {
    super(state);
    this.context = context;
    this.selectors = selectors;
  }

  /**
   * Non-reactive values such as refs, callbacks, etc.
   * Unlike `state`, this property can be accessed directly.
   */

  /**
   * Keeps track of which properties are controlled.
   */
  controlledValues = (() => new Map())();
  /**
   * Synchronizes a single external value into the store during layout phase.
   */
  useSyncedValue(key, value) {
    useIsoLayoutEffect(() => {
      if (this.state[key] !== value) {
        this.set(key, value);
      }
    }, [key, value]);
  }

  /**
   * Synchronizes a single external value into the store during layout phase and
   * cleans it up (sets to `undefined`) on unmount.
   */
  useSyncedValueWithCleanup(key, value) {
    useIsoLayoutEffect(() => {
      if (this.state[key] !== value) {
        this.set(key, value);
      }
      return () => {
        this.set(key, undefined);
      };
    }, [key, value]);
  }

  /**
   * Synchronizes multiple external values into the store during layout phase.
   */
  useSyncedValues(props) {
    useIsoLayoutEffect(() => {
      this.apply(props);
    }, [props]);
  }

  /**
   * Registers a controllable prop pair (`controlled`, `defaultValue`) for a specific key.
   * - If `controlled` is non-undefined, the key is marked as controlled and the store's
   *   state at `key` is updated to match `controlled`. Local writes to that key are ignored.
   * - If `controlled` is undefined, the key is marked as uncontrolled. The store's state
   *   is initialized to `defaultValue` on first render and can be updated with local writes.
   */
  useControlledProp(key, controlled, defaultValue) {
    const isControlled = controlled !== undefined;
    if (process.env.NODE_ENV !== 'production') {
      const previouslyControlled = this.controlledValues.get(key);
      if (previouslyControlled !== undefined && previouslyControlled !== isControlled) {
        console.error(`A component is changing the ${isControlled ? '' : 'un'}controlled state of ${key.toString()} to be ${isControlled ? 'un' : ''}controlled. Elements should not switch from uncontrolled to controlled (or vice versa).`);
      }
    }
    if (!this.controlledValues.has(key)) {
      // First time initialization
      this.controlledValues.set(key, isControlled);
      if (!isControlled && !Object.is(this.state[key], defaultValue)) {
        super.update({
          ...this.state,
          [key]: defaultValue
        });
      }
    }
    useIsoLayoutEffect(() => {
      if (isControlled && !Object.is(this.state[key], controlled)) {
        // Set the internal state to match the controlled value.
        super.update({
          ...this.state,
          [key]: controlled
        });
      }
    }, [key, controlled, defaultValue, isControlled]);
  }

  /**
   * Sets a specific key in the store's state to a new value and notifies listeners if the value has changed.
   * If the key is controlled (registered via {@link useControlledProp} with a non-undefined value),
   * the update is ignored and no listeners are notified.
   *
   * @param key The state key to update.
   * @param value The new value to set for the specified key.
   */
  set(key, value) {
    if (this.controlledValues.get(key) === true) {
      // Ignore updates to controlled values
      return;
    }
    super.set(key, value);
  }

  /**
   * Merges the provided changes into the current state and notifies listeners if there are changes.
   * Controlled keys are filtered out and not updated.
   *
   * @param values An object containing the changes to apply to the current state.
   */
  apply(values) {
    const newValues = {
      ...values
    };
    for (const key in newValues) {
      if (this.controlledValues.get(key) === true) {
        // Ignore updates to controlled values
        delete newValues[key];
      }
    }
    super.apply(newValues);
  }

  /**
   * Updates the entire store's state and notifies all registered listeners.
   * Controlled keys are left unchanged; only uncontrolled keys from `newState` are applied.
   *
   * @param newState The new state to set for the store.
   */
  update(newState) {
    const newValues = {
      ...newState
    };
    for (const key in newValues) {
      if (this.controlledValues.get(key) === true) {
        // Ignore updates to controlled values
        delete newValues[key];
      }
    }
    super.update({
      ...this.state,
      ...newValues
    });
  }

  /**
   * Returns a value from the store's state using a selector function.
   * Used to subscribe to specific parts of the state.
   * This methods causes a rerender whenever the selected state changes.
   *
   * @param key Key of the selector to use.
   */
  useState(key) {
    if (!this.selectors) {
      throw new Error('Base UI: selectors are required to call useState.');
    }
    return useStore(this, this.selectors[key]);
  }

  /**
   * Wraps a function with `useEventCallback` to ensure it has a stable reference
   * and assigns it to the context.
   *
   * @param key Key of the event callback. Must be a function in the context.
   * @param fn Function to assign.
   */
  useContextCallback(key, fn) {
    const stableFunction = useEventCallback(fn ?? NOOP);
    this.context[key] = stableFunction;
  }

  /**
   * Returns a stable setter function for a specific key in the store's state.
   * It's commonly used to pass as a ref callback to React elements.
   * @param key Key of the state to set.
   */
  getElementSetter(key) {
    return React.useCallback(element => {
      this.set(key, element);
    }, [key]);
  }
}